import { openDB, type DBSchema } from 'idb'
import type { Project, WorkItem } from '../shared/types'
import { ApiError } from './api'

export interface PendingMutation {
  id: string
  method: 'POST' | 'PATCH' | 'DELETE'
  path: string
  body?: unknown
  createdAt: number
  attempts: number
  nextAttemptAt: number
  state: 'pending' | 'failed' | 'conflict'
  error?: string
}

interface BoardSnapshot {
  key: string
  items: WorkItem[]
  projects?: Project[]
  updatedAt: number
}

interface NnnDatabase extends DBSchema {
  cache: { key: string; value: BoardSnapshot }
  mutations: {
    key: string
    value: PendingMutation
    indexes: { 'by-created': number }
  }
}

const dbPromise = openDB<NnnDatabase>('nownextnever-resilience', 2, {
  upgrade(db) {
    if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'key' })
    if (!db.objectStoreNames.contains('mutations')) {
      const mutations = db.createObjectStore('mutations', { keyPath: 'id' })
      mutations.createIndex('by-created', 'createdAt')
    }
  },
})

export function retryDelay(attempts: number) {
  return Math.min(60_000, 1000 * 2 ** Math.min(Math.max(0, attempts), 6))
}

export function mutationQueueState(
  mutations: PendingMutation[],
  online: boolean,
): 'saved' | 'saving' | 'offline' | 'failed' | 'conflict' {
  if (!mutations.length) return 'saved'
  if (!online) return 'offline'
  if (mutations.some((item) => item.state === 'conflict')) return 'conflict'
  if (mutations.some((item) => item.state === 'failed')) return 'failed'
  return 'saving'
}

export async function cacheItems(boardId: string, items: WorkItem[]) {
  const db = await dbPromise
  const transaction = db.transaction('cache', 'readwrite')
  const existing = await transaction.store.get(boardId)
  await transaction.store.put({
    key: boardId,
    items,
    projects: existing?.projects ?? [],
    updatedAt: Date.now(),
  })
  await transaction.done
}

export async function readCachedItems(boardId: string): Promise<WorkItem[]> {
  const db = await dbPromise
  const items = (await db.get('cache', boardId))?.items ?? []
  return items.map((item) => ({ ...item, tags: item.tags ?? [] }))
}

export async function cacheProjects(boardId: string, projects: Project[]) {
  const db = await dbPromise
  const transaction = db.transaction('cache', 'readwrite')
  const existing = await transaction.store.get(boardId)
  await transaction.store.put({
    key: boardId,
    items: existing?.items ?? [],
    projects,
    updatedAt: Date.now(),
  })
  await transaction.done
}

export async function readCachedProjects(boardId: string): Promise<Project[]> {
  return (await (await dbPromise).get('cache', boardId))?.projects ?? []
}

export async function queueMutation(
  mutation: Omit<PendingMutation, 'createdAt' | 'attempts' | 'nextAttemptAt' | 'state'>,
) {
  const db = await dbPromise
  await db.put('mutations', {
    ...mutation,
    createdAt: Date.now(),
    attempts: 0,
    nextAttemptAt: Date.now(),
    state: 'pending',
  })
}

export async function pendingMutations() {
  return (await dbPromise).getAllFromIndex('mutations', 'by-created')
}

export async function drainMutationQueue(onChange?: () => void) {
  if (!navigator.onLine) return { pending: (await pendingMutations()).length, failed: 0 }
  const db = await dbPromise
  const mutations = await db.getAllFromIndex('mutations', 'by-created')
  let failed = 0
  for (const mutation of mutations) {
    if (mutation.state === 'conflict' || mutation.nextAttemptAt > Date.now()) continue
    try {
      const response = await fetch(`/api/v1${mutation.path}`, {
        method: mutation.method,
        credentials: 'include',
        headers: mutation.body === undefined ? undefined : { 'Content-Type': 'application/json' },
        body: mutation.body === undefined ? undefined : JSON.stringify(mutation.body),
      })
      if (response.ok) {
        await db.delete('mutations', mutation.id)
        onChange?.()
        continue
      }
      const responseBody = (await response.json().catch(() => null)) as any
      if (response.status === 409) {
        await db.put('mutations', {
          ...mutation,
          state: 'conflict',
          error: responseBody?.error?.message ?? 'Conflict',
        })
      } else if (response.status >= 400 && response.status < 500) {
        await db.put('mutations', {
          ...mutation,
          state: 'failed',
          error: responseBody?.error?.message ?? `Request failed (${response.status})`,
        })
        failed += 1
      } else {
        throw new Error(`Temporary server failure (${response.status})`)
      }
    } catch (error) {
      const attempts = mutation.attempts + 1
      const delay = retryDelay(attempts)
      await db.put('mutations', {
        ...mutation,
        attempts,
        nextAttemptAt: Date.now() + delay,
        state: attempts >= 8 ? 'failed' : 'pending',
        error: error instanceof Error ? error.message : 'Network error',
      })
      if (attempts >= 8) failed += 1
      break
    }
  }
  const remaining = await db.getAll('mutations')
  return { pending: remaining.length, failed }
}

export async function retryAllMutations() {
  const db = await dbPromise
  const mutations = await db.getAll('mutations')
  for (const mutation of mutations) {
    await db.put('mutations', {
      ...mutation,
      state: 'pending',
      attempts: 0,
      nextAttemptAt: Date.now(),
      error: undefined,
    })
  }
  return drainMutationQueue()
}

export async function resilientMutation<T>(mutation: {
  id?: string
  method: PendingMutation['method']
  path: string
  body?: unknown
  optimistic: T
}): Promise<{ value: T; queued: boolean }> {
  try {
    const response = await fetch(`/api/v1${mutation.path}`, {
      method: mutation.method,
      credentials: 'include',
      headers: mutation.body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: mutation.body === undefined ? undefined : JSON.stringify(mutation.body),
    })
    const payload = (await response.json().catch(() => null)) as any
    if (!response.ok) {
      throw new ApiError(
        payload?.error?.message ?? `Request failed (${response.status})`,
        response.status,
        payload?.error?.code ?? 'REQUEST_FAILED',
        payload?.error?.details,
      )
    }
    return { value: payload.data as T, queued: false }
  } catch (error) {
    if (error instanceof ApiError) throw error
    await queueMutation({
      id: mutation.id ?? crypto.randomUUID(),
      method: mutation.method,
      path: mutation.path,
      body: mutation.body,
    })
    return { value: mutation.optimistic, queued: true }
  }
}
